import { Check, ChevronDown, LogOut, Monitor, Moon, Settings, ShieldCheck, Sun, UserCircle2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useLastWorkspace } from '../hooks/useLastWorkspace';
import { useTheme } from './ThemeProvider';
import { authApi } from '../lib/auth-api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/DropdownMenu';

/**
 * Logged-in user pill in the app header. Replaces the old static button.
 *
 * Logout is a single click — there is intentionally no "are you sure?"
 * confirmation. Re-logging in is cheap; an extra modal on every logout
 * is friction with no upside. Same reasoning the rest of the suite uses.
 *
 * No toast on logout either. The redirect to the landing page IS the
 * confirmation.
 */
export function UserMenu() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const { resolvedWorkspaceId } = useLastWorkspace();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: user } = useCurrentUser();
  const { theme, setTheme } = useTheme();

  if (!user) return null;

  async function onLogout() {
    try { await authApi.logout(); } catch { /* server-side cookie clear is best-effort */ }
    qc.clear();
    // Hard navigate so any in-memory app state (zustand stores, sockets) resets.
    window.location.assign('/');
  }

  const initial = (user.name || user.email || 'K').trim()[0]?.toUpperCase() ?? 'K';
  // Workspace owner/admin roles are tenant-scoped — they don't grant
  // access to the cross-tenant operations console. Only the server-side
  // `isPlyEZStaff` flag (email allowlist) does.
  const isAdmin = user.isPlyEZStaff;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-ink-100 dark:hover:bg-ink-800 transition-colors">
          {user.pictureUrl ? (
            <img src={user.pictureUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
          ) : (
            <div className="h-7 w-7 rounded-full bg-accent-500 text-white text-xs font-semibold flex items-center justify-center">
              {initial}
            </div>
          )}
          <span className="text-sm max-w-[140px] truncate">{user.name || user.email || '—'}</span>
          <ChevronDown size={14} className="text-ink-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>{user.email ?? user.name}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/app/profile')}>
          <UserCircle2 size={14} /> {t('userMenu.profile')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            // Settings is workspace-scoped. Prefer the current workspace,
            // then the last-active one, before giving up to '/app'.
            const targetWs = wsId ?? resolvedWorkspaceId;
            navigate(targetWs ? `/app/workspace/${targetWs}/settings` : '/app');
          }}
        >
          <Settings size={14} /> {t('userMenu.settings')}
        </DropdownMenuItem>
        {isAdmin ? (
          <DropdownMenuItem onSelect={() => navigate('/admin')}>
            <ShieldCheck size={14} /> {t('userMenu.admin')}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('userMenu.theme')}</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => setTheme('light')}>
          <Sun size={14} /> {t('userMenu.light')} {theme === 'light' && <Check size={12} className="ml-auto" />}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setTheme('dark')}>
          <Moon size={14} /> {t('userMenu.dark')} {theme === 'dark' && <Check size={12} className="ml-auto" />}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setTheme('system')}>
          <Monitor size={14} /> {t('userMenu.system')} {theme === 'system' && <Check size={12} className="ml-auto" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onLogout} destructive>
          <LogOut size={14} /> {t('userMenu.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
